import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "../layouts/DashboardLayout";
import SearchableDropdown from "../components/SearchableDropdown";
import ExcelExport from "../components/ExcelExport";
import { CentralLocationStockTable } from "../components/ReportTable";
import { centralLocationStockColumns } from "../config/reportColumns";
import {
  fetchGodowns,
  fetchProducts,
  fetchStockReport,
} from "../services/centralLocationStockService";

const CentralLocationStock = ({ onLogout }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [godowns, setGodowns] = useState([]);
  const [products, setProducts] = useState([]);

  const [selectedGodown, setSelectedGodown] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState([]);

  /* =======================
     LOAD DROPDOWNS AND REPORT ON MOUNT
  ======================== */
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        const [rawGodowns, rawProducts, reportData] = await Promise.all([
          fetchGodowns(),
          fetchProducts(),
          fetchStockReport([], []) // Pass empty arrays for all data
        ]);

        console.log("Raw Godowns:", rawGodowns);
        console.log("Raw Products:", rawProducts);
        console.log("Initial Report Data:", reportData);

        // Dig out the arrays safely
        const gArr = Array.isArray(rawGodowns) ? rawGodowns : (rawGodowns?.data || []);
        const pArr = Array.isArray(rawProducts) ? rawProducts : (rawProducts?.data || []);

        // Format options for dropdown
        const formatOption = (item) => {
          if (typeof item === 'object' && item !== null) {
            const val = Object.values(item)[0];
            return { value: val, label: val };
          }
          return { value: item, label: item };
        };

        setGodowns(gArr.map(formatOption));
        setProducts(pArr.map(formatOption));
        setData(Array.isArray(reportData) ? reportData : []);
        setLoading(false);
      } catch (err) {
        console.error("Failed to load initial data:", err);
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, []);

  /* =======================
     FETCH REPORT ON FILTER CHANGE
  ======================== */
  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const reportData = await fetchStockReport(selectedGodown, selectedProduct);
      setData(Array.isArray(reportData) ? reportData : []);
    } catch (error) {
      console.error("Error fetching report:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedGodown, selectedProduct]);

  /* =======================
     AUTO-FETCH WHEN FILTERS CHANGE
  ======================== */
  useEffect(() => {
    // Debounce the API call to prevent too many requests
    const timeoutId = setTimeout(() => {
      loadReport();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [selectedGodown, selectedProduct, loadReport]);

  /* =======================
     HANDLERS
  ======================== */
  const handleGodownChange = (selectedArray) => {
    setSelectedGodown(selectedArray || []);
  };

  const handleProductChange = (selectedArray) => {
    setSelectedProduct(selectedArray || []);
  };

  return (
    <DashboardLayout onLogout={onLogout}>
      <main className="p-6 bg-gray-100 h-screen overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">
            National Stock Report
          </h1>

          <ExcelExport
            data={data}
            columns={centralLocationStockColumns}
            reportTitle="Central Location Stock Report"
            fileName="central_location_stock_report.xlsx"
            reportFilters={{
              godown: selectedGodown,
              product: selectedProduct,
            }}
            groupKeyFields={["godown_name", "product_name"]}
            hiddenRepeatKeys={["grand_total_qty", "godown_name", "total_amount", "total_alt_qty", "product_name"]}
          />
        </div>

        <div className="flex flex-wrap gap-4 mb-4">
          <SearchableDropdown
            label="Godown"
            options={godowns}
            value={selectedGodown}
            onChange={handleGodownChange}
            isMulti={true}
            placeholder="Select Godowns..."
          />

          <SearchableDropdown
            label="Product"
            options={products}
            value={selectedProduct}
            onChange={handleProductChange}
            isMulti={true}
            placeholder="Select Products..."
          />
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">Loading...</div>
          </div>
        ) : (
          <CentralLocationStockTable reportData={data} />
        )}
      </main>
    </DashboardLayout>
  );
};

export default CentralLocationStock;